import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LoadingDots } from "web-ui";

import { type EventLogEntry, type Release } from "api/dashboardBackend";
import VersionSelect from "components/common/VersionSelect";
import styles from "components/release/ReleasePage.module.scss";
import ReadinessChecklist from "components/release/readiness-checklist/ReadinessChecklist";
import useReleaseSpecificScreen from "components/useReleaseSpecificScreen";
import { platformNames } from "util/platform";
import { getReadinessStatus } from "util/release";

const eventToString = (event: EventLogEntry) => {
  const fromState = (event.fromState ?? "unknown").toUpperCase();
  const fromVersion = event.fromVersion ? ` (${event.fromVersion})` : "";
  const toState = event.toState.toUpperCase();
  const toVersion = event.toVersion ? ` (${event.toVersion})` : "";
  const timestamp = new Date(event.timestamp * 1000).toLocaleString("en-US", {
    hour12: false,
    timeZone: "America/New_York",
    timeZoneName: "short",
  });
  return `${timestamp}: ${fromState}${fromVersion} -> ${toState}${toVersion}`;
};

const isNullish = (value: unknown) => value === null || value === undefined;

const releaseVersionString = (release: Release) => {
  const majorMinorPatch = `${release.majorMinor}.${release.patch}`;
  const build = isNullish(release.build) ? "" : `.${release.build}`;
  const versionSuffix = release.versionSuffix ?? "";
  return `${majorMinorPatch}${build}${versionSuffix}`;
};

const ReleasePage = () => {
  const { platform, version } = useParams();
  const navigate = useNavigate();

  const { errorComponent, isLoading, platformId, release } =
    useReleaseSpecificScreen(platform, version);

  if (errorComponent || !platformId) {
    return errorComponent;
  }

  const dailyActiveUsers = release?.readiness?.metrics.find(
    metric => metric.type === "daily_active_users",
  );

  const sizeMetrics = (release?.readiness?.metrics ?? [])
    .filter(metric => /app_(download|install)_size/.test(metric.type))
    .sort((a, b) => a.type.localeCompare(b.type));

  const eventLog = release?.releaseEventLog ?? [];

  return platformId && release?.readiness ? (
    <div className={styles.readiness}>
      <Link
        style={{
          color: "rgb(var(--color-eel))",
          fontSize: "40px",
          position: "fixed",
          right: "15px",
          top: "10px",
        }}
        title="Close release readiness"
        to="/"
      >
        ✕
      </Link>
      <div className={styles.readinessTitle}>
        Readiness Report for {platformNames[platformId]} {release.majorMinor}.
        {release.patch}
        {isNullish(release.build) ? "" : `.${release.build}`}
        {release.versionSuffix}
      </div>
      <div className={styles.versionSelectContainer}>
        <VersionSelect
          onChangeCallback={(vers: string) => {
            navigate(`/release/${platformId}/${vers}`);
          }}
          platformId={platformId}
          queryPrompt="Select another release version:"
          value={release.majorMinor}
        />
      </div>
      {release.readinessStatus && (
        <div className={styles.readinessSubtitle}>
          <span className={styles.imageStatus}>
            <img src={getReadinessStatus(release).imageStatus} />
          </span>
          {getReadinessStatus(release).title.trim()}
        </div>
      )}
      <ReadinessChecklist
        dailyActiveUsers={dailyActiveUsers}
        metricReadinessItems={release.readiness.metrics.filter(
          metric => metric.shouldShowInChecklist,
        )}
        otherReadinessItems={[
          release.readiness.releaseBlockers,
          release.readiness.signoff,
        ]}
        platformId={platformId}
        release={release}
        sizeMetrics={sizeMetrics}
        version={releaseVersionString(release)}
      />
      <div className={styles.readinessSubtitle}>
        <strong>Release Event Log</strong>
      </div>
      <ul className={styles.eventLog}>
        {eventLog.map((event, k) => (
          <li key={k}>{eventToString(event)}</li>
        ))}
      </ul>
    </div>
  ) : isLoading ? (
    <LoadingDots type="screen-white" />
  ) : null;
};

export default ReleasePage;
